import { useId, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  Select,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import { useBankAccounts, useImportStatement } from "@/api/queries";
import type { Id, StatementFormat, StatementImportResult } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { SegmentControl } from "@/components/SegmentControl";
import { DATE_FORMATS, guessColumns, guessDateFormat, parseCsv } from "./statementCsv";

const DELIMITERS = [
  { value: ",", label: "Comma" },
  { value: ";", label: "Semicolon" },
  { value: "\t", label: "Tab" },
];

const NONE = "";

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

const isOfx = (name: string) => /\.(ofx|qfx)$/i.test(name);

type Row = { index: number; cells: string[] };

/**
 * Reads a bank's statement into an account. OFX states its own shape. A CSV doesn't, so this asks: the
 * form starts from what the file's headings suggest, and nothing is sent until the person has said which
 * column is which. A date read the wrong way balances to the wrong day.
 */
export function ImportStatementDialog({
  accountId,
  onClose,
}: {
  accountId?: Id;
  onClose: () => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const { data: accounts } = useBankAccounts();
  const importing = useImportStatement();
  const [into, setInto] = useState(accountId ?? "");
  const [fileName, setFileName] = useState("");
  const [content, setContent] = useState("");
  const [delimiter, setDelimiter] = useState(",");
  const [hasHeader, setHasHeader] = useState(true);
  const [paired, setPaired] = useState(false);
  const [dateFormat, setDateFormat] = useState("");
  const [columns, setColumns] = useState<Record<string, string>>({});
  const [result, setResult] = useState<StatementImportResult | null>(null);

  const target = into || accounts?.[0]?.id || "";
  const ofx = isOfx(fileName);
  const rows = content !== "" && !ofx ? parseCsv(content, delimiter) : [];
  const width = rows[0]?.length ?? 0;
  const body = hasHeader ? rows.slice(1) : rows;
  const options: SelectOption[] = Array.from({ length: width }, (_, index) => ({
    value: String(index),
    label: hasHeader && rows[0]?.[index] ? rows[0][index]! : `Column ${index + 1}`,
  }));

  /** Starts the form from the file's headings. Everything stays changeable. */
  function fillIn(text: string, separator: string) {
    const [first, second] = parseCsv(text, separator, 2);
    const guessed = guessColumns(first ?? []);
    const text0 = (value: number | null) => (value === null ? NONE : String(value));
    setColumns({
      date: text0(guessed.date),
      description: text0(guessed.description),
      amount: text0(guessed.amount),
      debit: text0(guessed.debit),
      credit: text0(guessed.credit),
      reference: text0(guessed.reference),
      balance: text0(guessed.balance),
    });
    setPaired(guessed.amount === null && guessed.debit !== null && guessed.credit !== null);
    setDateFormat(guessDateFormat(second?.[guessed.date ?? 0] ?? ""));
  }

  const column = (key: string) =>
    (columns[key] ?? NONE) === NONE ? undefined : Number(columns[key]);

  const format = (): StatementFormat | null => {
    const date = column("date");
    const description = column("description");
    if (dateFormat === "" || date === undefined || description === undefined) return null;
    if (paired && (column("debit") === undefined || column("credit") === undefined)) return null;
    if (!paired && column("amount") === undefined) return null;
    return {
      dateFormat,
      dateColumn: date,
      descriptionColumn: description,
      delimiter,
      hasHeader,
      ...(paired
        ? { debitColumn: column("debit"), creditColumn: column("credit") }
        : { amountColumn: column("amount") }),
      referenceColumn: column("reference"),
      balanceColumn: column("balance"),
    };
  };

  const ready = target !== "" && content !== "" && (ofx || format() !== null);

  const columnSelect = (name: string, label: string, optional = false) => (
    <Select
      label={label}
      required={!optional}
      placeholder="Choose a column"
      options={optional ? [{ value: NONE, label: "None" }, ...options] : options}
      value={columns[name] ?? NONE}
      onChange={(details) => setColumns({ ...columns, [name]: chosen(details) })}
      showClearButton={false}
    />
  );

  const previewColumns: Array<ColumnDef<Row>> = options.map((option, index) => ({
    id: `c${index}`,
    header: String(option.label),
    cell: ({ row: { original } }) => original.cells[index] ?? "",
  }));

  if (result) {
    return (
      <Dialog open onClose={onClose} title="Statement imported" dismissLabel="Done">
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-fg">
            {result.linesImported} {result.linesImported === 1 ? "line" : "lines"} imported from{" "}
            {fileName}.
          </p>
          {result.problems.length > 0 ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "warning" }}
              description={
                result.problems.length === 1
                  ? "1 line couldn't be read, so it wasn't imported."
                  : `${result.problems.length} lines couldn't be read, so they weren't imported.`
              }
            />
          ) : null}
          {result.problems.length > 0 ? (
            <ul className="text-fg-muted flex flex-col gap-1 text-xs">
              {result.problems.slice(0, 20).map((problem) => (
                <li key={`${problem.lineNumber}-${problem.column}`}>
                  Line {problem.lineNumber}, {problem.column}: {problem.message}
                </li>
              ))}
            </ul>
          ) : null}
          {result.suspectedRepeats > 0 ? (
            <p className="text-warn-text text-xs">
              {result.suspectedRepeats} look the same as a line already on this account for that
              day. They were imported, because two identical payments can be real. Check them if you
              imported this file before.
            </p>
          ) : null}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Import statement"
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={!ready || importing.isPending}
          onClick={() =>
            importing.mutate(
              { accountId: target, fileName, content, format: ofx ? null : format() },
              { onSuccess: setResult },
            )
          }
        >
          {importing.isPending ? "Importing" : "Import statement"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {importing.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(importing.error as Error).message}
          />
        ) : null}
        <Select
          label="Into account"
          required
          options={(accounts ?? []).map((account) => ({ value: account.id, label: account.name }))}
          value={target}
          onChange={(details) => setInto(chosen(details))}
          showClearButton={false}
        />
        <div className="flex flex-col items-start gap-1">
          <span id={`${id}-label`} className="text-fg text-sm font-medium">
            Statement file · CSV or OFX
          </span>
          <input
            ref={input}
            type="file"
            hidden
            accept=".csv,.txt,.ofx,.qfx"
            aria-labelledby={`${id}-label`}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              void file.text().then((text) => {
                setFileName(file.name);
                setContent(text);
                if (!isOfx(file.name)) fillIn(text, delimiter);
              });
            }}
          />
          <Button
            colorScheme="default"
            variant="outline"
            size="sm"
            leftIcon="Upload"
            onClick={() => input.current?.click()}
          >
            {fileName === "" ? "Choose a file" : "Choose another file"}
          </Button>
          {fileName ? <span className="text-fg-muted text-xs">{fileName}</span> : null}
          {ofx ? (
            <span className="text-fg-muted text-xs">
              An OFX file states its own layout, so there's nothing to fill in.
            </span>
          ) : null}
        </div>

        {rows.length > 0 ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Select
                label="Separated by"
                options={DELIMITERS}
                value={delimiter}
                onChange={(details) => {
                  const next = chosen(details);
                  if (!next) return;
                  setDelimiter(next);
                  fillIn(content, next);
                }}
                showClearButton={false}
              />
              <Select
                label="Dates are written as"
                required
                placeholder="Choose a format"
                options={DATE_FORMATS.map((value) => ({ value, label: value }))}
                value={dateFormat}
                onChange={(details) => setDateFormat(chosen(details))}
                showClearButton={false}
              />
            </div>
            <Checkbox
              label="The first row is headings"
              checked={hasHeader}
              onCheckedChange={(details) => setHasHeader(details.checked === true)}
            />
            <div className="grid gap-3 md:grid-cols-2">
              {columnSelect("date", "Date")}
              {columnSelect("description", "Description")}
            </div>
            <SegmentControl<"one" | "pair">
              label="How amounts are shown"
              segments={[
                { value: "one", label: "One amount column" },
                { value: "pair", label: "Debit and credit columns" },
              ]}
              value={paired ? "pair" : "one"}
              onChange={(next) => setPaired(next === "pair")}
            />
            {paired ? (
              <div className="grid gap-3 md:grid-cols-2">
                {columnSelect("debit", "Debit · money out")}
                {columnSelect("credit", "Credit · money in")}
              </div>
            ) : (
              columnSelect("amount", "Amount · positive is money in")
            )}
            <div className="grid gap-3 md:grid-cols-2">
              {columnSelect("reference", "Reference", true)}
              {columnSelect("balance", "Running balance", true)}
            </div>
            <div className="overflow-x-auto">
              <DataTable
                aria-label="First rows of the file"
                size="sm"
                enableSorting={false}
                columns={previewColumns}
                data={body.slice(0, 3).map((cells, index) => ({ index, cells }))}
                getRowId={(row) => String(row.index)}
                pagination={{ mode: "manual", show: false }}
              />
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
